/*
 * Copyright 2022 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import {
    AppLoadingIndicatorV1,
    useAuthProviderV1,
} from "@startree-ui/platform-ui";
import React, { FunctionComponent, lazy, Suspense } from "react";
import { Route, Switch } from "react-router-dom";
import { AppRouteV2 } from "../../utils/routes/routes.util";

const ConnectionsRouter = lazy(() =>
    import(
        /* webpackChunkName: "connections-router" */ "../connections/connections.router"
    ).then((module) => ({ default: module.ConnectionsRouter }))
);

const DatasetsRouter = lazy(() =>
    import(
        /* webpackChunkName: "datasets-router" */ "../datasets/datasets.router"
    ).then((module) => ({ default: module.DatasetsRouter }))
);

const GeneralAuthenticatedRouter = lazy(() =>
    import(
        /* webpackChunkName: "general-authenticated-router" */ "../general-authenticated/general-authenticated.router"
    ).then((module) => ({ default: module.GeneralAuthenticatedRouter }))
);

const GeneralUnauthenticatedRouter = lazy(() =>
    import(
        /* webpackChunkName: "general-unauthenticated-router" */ "../general-unauthenticated/general-unauthenticated.router"
    ).then((module) => ({ default: module.GeneralUnauthenticatedRouter }))
);

const OverviewPage = lazy(() =>
    import(
        /* webpackChunkName: "page-not-found-page" */ "../../pages/overview-page/overview-page.component"
    ).then((module) => ({ default: module.OverviewPage }))
);

export const AppRouter: FunctionComponent = () => {
    const { authDisabled, authenticated } = useAuthProviderV1();
    const shouldLoadRoutes = authDisabled || authenticated;

    if (shouldLoadRoutes) {
        // Authenticated or auth disabled + workspace selected
        return (
            <Suspense fallback={<AppLoadingIndicatorV1 />}>
                <Switch>
                    {/* Direct all connections paths to connections router */}
                    <Route
                        component={ConnectionsRouter}
                        path={AppRouteV2.CONNECTIONS}
                    />

                    {/* Direct all datasets paths to datasets router */}
                    <Route
                        component={DatasetsRouter}
                        path={AppRouteV2.DATASETS}
                    />

                    {/* V2 Routes */}
                    <Route
                        exact
                        component={OverviewPage}
                        path={AppRouteV2.OVERVIEW}
                    />

                    {/* Direct all connection paths to datasets router */}
                    <Route
                        component={ConnectionsRouter}
                        path={AppRouteV2.CONNECTION}
                    />

                    {/* Direct `/datasets` path to datasets router */}
                    <Route
                        exact
                        component={DatasetsRouter}
                        path={AppRouteV2.DATASETS}
                    />

                    {/* Direct all dataset paths to datasets router */}
                    <Route
                        component={DatasetsRouter}
                        path={AppRouteV2.DATASET}
                    />

                    {/* Direct all other paths to general authenticated router */}
                    <Route component={GeneralAuthenticatedRouter} />
                </Switch>
            </Suspense>
        );
    }

    // Not (authenticated or auth enabled + workspace enabled)
    return (
        <Suspense fallback={<AppLoadingIndicatorV1 />}>
            {/* Direct all paths to general unauthenticated router */}
            <GeneralUnauthenticatedRouter />
        </Suspense>
    );
};
